import React, { forwardRef } from "react";
import classNames from "classnames";

import defaultStyles from "./main.module.scss";

type iconTypes = "radio" | "check" | "switch" | "radioOutline";
type switchIconPlacement = "start" | "end";
type switchToggleSize = "small" | "medium" | "large";
type iconClassMapping = {
  [icon in iconTypes]: string;
};

type switchToggleSizeClassMapping = {
  [size in switchToggleSize]: string;
};

export interface LabelProps extends React.ComponentPropsWithoutRef<"label"> {
  iconType?: iconTypes;

  switchIconPlacement?: switchIconPlacement;
  switchSize?: switchToggleSize;

  /**
   * Whether to show the icon or not
   */
  showIcon?: boolean;
  /**
   * Add border around label
   */
  border?: boolean;
  /**
   * Is this label checked? If true, display icon
   */
  checked?: boolean;
  styles?: ModularStyles;
  enablePredefinedClass?: boolean;
}

const Label = forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      iconType = "radio",
      switchIconPlacement = "start",
      switchSize = "small",
      showIcon = true,
      border = false,
      className = "",
      htmlFor = "",
      tabIndex = "",
      children = "",
      checked = false,
      styles = defaultStyles,
      style,
      title,
      "aria-disabled": ariaDisabled,
      onClick,
      enablePredefinedClass = false,
    },
    ref,
  ) => {
    const ICON_CLASS_MAPPING: iconClassMapping = {
      radio: styles.radioIcon,
      check: styles.checkboxIcon,
      switch: `${styles.switchIcon} ${switchIconPlacement == "start" ? "" : styles.iconEnd}`,
      radioOutline: styles.radioOutlineIcon,
    };

    const iconClass = ICON_CLASS_MAPPING[iconType] ? ICON_CLASS_MAPPING[iconType] : "";

    const SWITCH_SIZE_CLASS_MAPPING: switchToggleSizeClassMapping = {
      small: styles.smallIcon,
      medium: styles.mediumIcon,
      large: styles.largeIcon,
    };

    const switchSizeClass = SWITCH_SIZE_CLASS_MAPPING[switchSize] ?? "";

    const labelClassName = classNames(className, {
      [styles.withBorder]: border,
      [iconClass]: showIcon,
      [styles.checked]: checked,
      [styles.labelStyles]: enablePredefinedClass,
    });

    return (
      children && (
        <label
          title={title}
          aria-disabled={ariaDisabled}
          htmlFor={htmlFor}
          tabIndex={tabIndex}
          style={style}
          className={labelClassName}
          ref={ref}
          onClick={onClick}
        >
          {iconType == "switch" && (
            <div className={`${styles.toggleView} ${switchSizeClass}`}></div>
          )}
          {children}
        </label>
      )
    );
  },
);

export default Label;
